import {Dialog} from '@reach/dialog';
import {DialogFooter, DialogTitle} from '@common/components/Dialog';
import {
  ErrorMessage,
  FormGroup,
  FormItemGroup,
  Input,
  RequireMarker,
  TimesInput,
  Radio,
} from '@common/components/form-items';
import {Controller, useFormContext} from 'react-hook-form';
import Button, {ButtonGroup} from '@common/components/Button';
import Select from '@common/components/Select';
import {Shift, ShiftSchedule, cycleTypeOptions, calendarTypeOptions, CalendarType} from '@gakki/api/types';
import BaselineInfo from '@gakki/components/info-tip/baseline-info';
import CalendarInfo from '@gakki/components/info-tip/calendar-info';
import GenerateScheduleInfo from '@gakki/components/info-tip/generate-schedule-info';
import {DevTool} from '@hookform/devtools';
import { useEffect } from 'react';

type ShiftFormProps = {
  isOpen?: boolean;
  onClose: () => void;
  onConfirm: (shift: Shift) => void;
  shiftSchedules: ShiftSchedule[];
  baseLineEditable?: boolean;
  triggerValidate?: boolean;
};

function ShiftForm(props: ShiftFormProps) {
  const {
    isOpen,
    shiftSchedules,
    onClose,
    onConfirm,
    baseLineEditable = true,
    triggerValidate = false,
  } = props;
  const {register, control, watch, handleSubmit, errors, getValues, setValue, trigger, formState} =
    useFormContext<Shift>();
  const id = watch('id');
  const selectedShifts = watch('shiftSchedules');
  const cycleType = watch('cycleType');
  const currentShiftSchedules = shiftSchedules.filter((shift) => shift.cycleType === cycleType)

  useEffect(() => {
    if (triggerValidate) {
      setTimeout(() => { // trigger時欄位未註冊，先用setTimeout延遲
        trigger()
      }, 1)
    }
  }, [triggerValidate, trigger])

  const checkIsValidShift = (shift: any) => {
    return Boolean(currentShiftSchedules.find(s => s.id === shift.shiftScheduleId))
  }

  return (
    <Dialog isOpen={isOpen} style={{width: 650}} aria-label="shift-dialog">
      <DialogTitle title={`${id ? '編輯' : '新增'}班別`} onClose={onClose} />
      <form className={'shift-form'} onSubmit={handleSubmit(onConfirm)}>
        <input
          ref={register}
          name={'standardShiftSchedule.shiftScheduleId'}
          type={'hidden'}
        />
        <input
          ref={register}
          name={'standardShiftSchedule.shiftScheduleName'}
          type={'hidden'}
        />
        <input type={'hidden'} ref={register} name={'id'} />
        <div
          className={
            'flex flex-col border-b border-b-[#e9eced] mb-5 px-3 pt-[18px] pb-5'
          }
        >
          <FormGroup columnTemplates={1}>
            <FormItemGroup>
              <label htmlFor={'name'}>
                班別名稱
                <RequireMarker />
              </label>
              <Input
                data-test-id={'store-name'}
                name={'storeName'}
                id={'storeName'}
                placeholder={'請輸入班別名稱'}
                autoFocus
                error={errors.storeName && errors.storeName.message}
                ref={register({required: '班別名稱為必填'})}
              />
              <ErrorMessage>
                {errors.storeName && errors.storeName.message}
              </ErrorMessage>
            </FormItemGroup>
          </FormGroup>
          <FormGroup columnTemplates={1} data-test-id={'cycle-type'}>
            <FormItemGroup>
              <label htmlFor={'cycle-type'}>
                重複時間
                <RequireMarker />
              </label>
              <Controller
                control={control}
                render={({onChange, value}) => (
                  <Select
                    name={'cycle-type'}
                    id={'cycle-type'}
                    placeholder={'請選擇重複時間'}
                    options={cycleTypeOptions}
                    error={errors.cycleType}
                    onChange={(option: {value: string}) => {
                      onChange(option.value);
                      setValue('shiftSchedules', []);
                      setValue('defaultShiftSchedule', null);
                    }}
                    value={cycleTypeOptions.find((o) => o.value === value)}
                  />
                )}
                name="cycleType"
                rules={{
                  validate: {
                    // 後端使用 -1 取代 null
                    isRequired: v => typeof v !== 'number' || Number(v) < 0 ? '重複時間為必填' : true,
                  }
                }}
              />
            </FormItemGroup>
          </FormGroup>
          <FormGroup columnTemplates={1} data-test-id={'shift-schedules'}>
            <FormItemGroup>
              <label htmlFor={'shiftSchedules'}>
                適用班次
                <RequireMarker />
              </label>
              <Controller
                name={'shiftSchedules'}
                render={({onChange, value}) => (
                  <Select
                    isMulti
                    placeholder={'請選擇適用班次'}
                    options={currentShiftSchedules.map(s => ({
                      shiftScheduleId: s.id,
                      shiftScheduleName: s.name,
                    }))}
                    getOptionLabel={(o: any) => o.shiftScheduleName}
                    getOptionValue={(o: any) => o.shiftScheduleId}
                    onChange={(o: any) => {
                      const {defaultShiftSchedule} = getValues();
                      onChange(o);
                      if (
                        o?.filter(
                          (shift: any) =>
                            shift?.shiftScheduleId ===
                            defaultShiftSchedule?.shiftScheduleId,
                        ).length === 0
                      ) {
                        setValue('defaultShiftSchedule', null);
                      }
                      if (errors.shiftSchedules) {
                        trigger('shiftSchedules')
                        trigger('defaultShiftSchedule')
                      }
                    }}
                    value={value}
                    error={
                      //@ts-ignore
                      errors.shiftSchedules && errors.shiftSchedules?.message
                    }
                    noOptionsMessage={() => currentShiftSchedules.length > 0 ? '請先建立班次' : '請先選擇重複時間'}
                    customStyles={{
                      multiValue: (styles: any, { data }: any) => {
                        return {
                          ...styles,
                          backgroundColor: checkIsValidShift(data) ? 'hsl(0, 0%, 90%)' : '#FAEFEF',
                        };
                      },
                      multiValueLabel: (styles: any, { data }: any) => ({
                        ...styles,
                        color: checkIsValidShift(data) ? 'hsl(0, 0%, 20%)' : '#be3a3a',
                      }),
                      multiValueRemove: (styles: any, { data }: any) => ({
                        ...styles,
                        color: checkIsValidShift(data) ? 'hsl(0, 0%, 20%)' : '#be3a3a',
                      }),
                    }}
                  />
                )}
                control={control}
                rules={{
                  validate: {
                    isRequired: v => !v || v.length === 0 ? '適用班次為必填' : true,
                    hasInvalidShift: selectedValue => {
                      const notFindShift = (shift: any) => !currentShiftSchedules.find(s => s.id === shift.shiftScheduleId)
                      return selectedValue.some(notFindShift) ? '"適用班次的重複時間" 與 "班別的重複時間" 不一致，請重新確認適用班次' : true
                    }
                  }
                }}
              />
            </FormItemGroup>
          </FormGroup>
          <FormGroup columnTemplates={2} data-test-id={'base-line'}>
            <FormItemGroup>
              <label htmlFor={'baseLine'}>
                班別工時基準
                <RequireMarker />
                <BaselineInfo />
              </label>
              <Controller
                control={control}
                render={({onChange, value}) => (
                  <TimesInput
                    disabled={!baseLineEditable}
                    onChange={onChange}
                    value={value}
                    error={errors.baseLine && errors.baseLine.message}
                  />
                )}
                name="baseLine"
                rules={{required: '班別工時基準為必填'}}
              />
            </FormItemGroup>
            <FormItemGroup data-tset-id={'default-shift-schedule'}>
              <label htmlFor={'color'}>
                預設班次
                <RequireMarker />
              </label>
              <Controller
                render={({onChange, value}) => (
                  <Select
                    placeholder={'請選擇預設班次'}
                    options={selectedShifts || []}
                    getOptionLabel={(o: any) => o.shiftScheduleName}
                    getOptionValue={(o: any) => o.shiftScheduleId}
                    onChange={(o: any) => {
                      onChange(o)
                      if (errors.defaultShiftSchedule) {
                        trigger('defaultShiftSchedule')
                      }
                    }}
                    value={value}
                    error={
                      errors.defaultShiftSchedule &&
                      //@ts-ignore
                      errors.defaultShiftSchedule.message
                    }
                    noOptionsMessage={() => '請先選擇適用班次'}
                  />
                )}
                name="defaultShiftSchedule"
                control={control}
                rules={{required: '預設班次為必填'}}
              />
            </FormItemGroup>
          </FormGroup>
          <FormGroup columnTemplates={2}>
            <FormItemGroup data-tset-id={'shift-schedule-calendar-type'}>
              <label htmlFor={'color'}>
                行事曆
                <RequireMarker />
                <CalendarInfo />
              </label>
              <Controller
                render={({onChange, value}) => (
                  <Select
                    placeholder={'請選擇行事曆'}
                    options={calendarTypeOptions}
                    onChange={(o: { value: CalendarType}) => {
                      if (o.value === CalendarType.emptyCalendar) {
                        setValue('isAutoGenerateCalendar', false);
                      } else {
                        setValue('isAutoGenerateCalendar', true);
                      }
                      onChange(o.value)
                      if (errors.isAutoGenerateCalendar) {
                        trigger('isAutoGenerateCalendar')
                      }
                    }}
                    value={calendarTypeOptions.find(o => o.value === value)}
                    error={
                      errors.shiftScheduleCalendarType &&
                      //@ts-ignore
                      errors.shiftScheduleCalendarType.message
                    }
                  />
                )}
                name="shiftScheduleCalendarType"
                control={control}
                rules={{
                  validate: {
                    // 後端使用 -1 取代 null
                    isRequired: v => typeof v !== 'number' || Number(v) < 0 ? '行事曆為必填' : true,
                  }
                }}
              />
            </FormItemGroup>
            <FormItemGroup data-tset-id={'is-auto-generate-calendar'}>
              <label htmlFor={'color'}>
                是否自動產生班表
                <RequireMarker />
                <GenerateScheduleInfo />
              </label>
              <Controller
                control={control}
                name={'isAutoGenerateCalendar'}
                render={({onChange, value}) => (
                  <div className="flex items-center gap-[50px] text-[15px] h-[38px]">
                    <Radio
                      onChange={e => {
                        onChange(true);
                      }}
                      value={'true'}
                      checked={String(value) === 'true'}
                    >
                      是
                    </Radio>
                    <Radio
                      value={'false'}
                      checked={String(value) === 'false'}
                      onChange={e => {
                        onChange(false);
                      }}
                    >
                      否
                    </Radio>
                  </div>
                )}
                rules={{
                  validate: {
                    isRequired: v => typeof v !== 'boolean' ? '是否自動產生班表為必填' : true,
                  }
                }}
              />
              <ErrorMessage>
                {errors.isAutoGenerateCalendar && errors.isAutoGenerateCalendar.message}
              </ErrorMessage>
            </FormItemGroup>
          </FormGroup>
        </div>
        <DialogFooter>
          <ButtonGroup direction={'rtl'}>
            <Button type={'submit'} variant={'cta'} className={'confirm'}>
              確定
            </Button>
            <Button variant={'primary'} className={'close'} onClick={onClose}>
              取消
            </Button>
          </ButtonGroup>
        </DialogFooter>
      </form>
      <DevTool control={control}/>
    </Dialog>
  );
}

export default ShiftForm;
